import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TypeAheadComponent } from '@components/type-ahead/type-ahead.component';
import { MatInputModule } from '@angular/material/input';

import { ButtonComponent } from '@components/index';
import { FieldSet, FormFieldEvent, FormData, FieldSetValidators, StartEndTimeRange } from '@models/index';
import { MultiSelectSelectionValidator, DependencyParameterValidator } from '@validators/index';
import { ReportService, ApiService } from '@services/index';

import { DateRangeFieldComponent } from './date-range-field/date-range-field.component';
import { RadioButtonFieldComponent } from './radio-button-field/radio-button.component';
import { SelectFieldComponent } from './select-field/select-field.component';
import { TextFieldComponent } from './text-field/text-field.component';
import { DateFieldComponent } from './date-field/date-field.component';
import { TextAreaComponent } from './text-area/text-area.component';


@Component({
  selector: 'app-form',
  standalone: true,
  imports: [
    /* COMPONENTS */
    ButtonComponent,
    TypeAheadComponent,
    DateRangeFieldComponent,
    DateFieldComponent,
    RadioButtonFieldComponent,
    SelectFieldComponent,
    TextFieldComponent,
    TextAreaComponent,
    /* MODULES */
    MatInputModule,
    CommonModule,
    ReactiveFormsModule
],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
})
export class FormComponent implements OnInit {
  @Input() submitButtonContent = "Submit"
  @Input() fieldSets: FieldSet[] = [];
  @Output() formSubmit = new EventEmitter<FormData>();
  public formError = false;
  public reportsForm!: FormGroup;
  public submitCount = 0;

  constructor(
    private formBuilder: FormBuilder,
    private reportService: ReportService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.buildForm();
  }

  public buildForm(): void {
    const formGroup = {};
    const allFields = this.fieldSets.flatMap(fs => fs.fields ?? []);

    if (!this.fieldSets) return;
    const fieldSetValidators: FieldSetValidators[] = [];
    this.fieldSets.forEach(fieldSet => {
      let fieldNames: string[] = [];

      if (fieldSet.required > 0) {
        const fields = fieldSet.fields ?? [];
        fieldNames = fields.map(field => field.name);

        fieldSetValidators.push({
          fieldSetName: fieldSet.name,
          minRequired: fieldSet.required,
          fieldNames
        })
      }

      fieldSet.fields?.forEach(field => {
        const formControlValidators: ValidatorFn[] = [];

        if (field.required === true) {
          formControlValidators.push(Validators.required);
        }
        if (field.countOfSelect) {
          formControlValidators.push(MultiSelectSelectionValidator.maxSelectionLimit(field.countOfSelect) as ValidatorFn);
        }
        formGroup[field.name] = [field.value, formControlValidators];

      })
    });

    this.reportsForm = this.formBuilder.group(formGroup, {
      validators: [DependencyParameterValidator.dependencyFieldValidator(allFields),
        fieldSetValidators.length ? this.fieldSetsValidator(fieldSetValidators) : null
      ].filter(Boolean)
    });
  }

  public submit(): void {
    this.submitCount++;

    if (this.reportsForm.valid && this.fieldSets) {

      const formData = {
        fieldData: this.fieldSets,
        formValues: this.reportsForm.value
      };

      this.formSubmit.emit(formData);

    } else {
      this.formError = true;
      console.warn("Form is not valid. Please check the form for errors.");
    }
  }

public updateControlValue(event: FormFieldEvent): void {
  const control = this.reportsForm.get(event.name);

  if (!control) {
    console.warn(`Form control with name ${event.name} does not exist.`);
    return;
  }

  const isTimeRange = (value: any): value is StartEndTimeRange =>
    value && typeof value === 'object' && !Array.isArray(value) && 'start' in value && 'end' in value;

  if (isTimeRange(event.value)) {
    const { start, end } = event.value;
    control.setValue(start || end ? event.value : '');
  } else {
    control.setValue(event.value);
  }

  if (event.errors) control.setErrors(event.errors);

  const changedField = this.fieldSets
    .flatMap(fs => fs.fields ?? [])
    .find(f => f.name === event.name);

  const userSelectedDefault =
    (event.value === "" || event.value === null || event.value === "-1");

  // If user select default, update dependent fields to default
  if (changedField?.reloadDataOnChange && userSelectedDefault) {
    const updatedFieldSets = this.fieldSets.map(fieldSet => {
      fieldSet.fields = (fieldSet.fields ?? []).map(field => {

        if (field.dependsOn === changedField.name) {
          field.options = [{ id: -1, value: `Select ${field.label}` }];

          const depCtrl = this.reportsForm.get(field.name);
          if (depCtrl) {
            depCtrl.setValue("");
            depCtrl.markAsPristine();
            depCtrl.markAsUntouched();
          }
        }

        return field;
      });

      return fieldSet;
    });

    this.fieldSets = [...updatedFieldSets];
    this.reportService.fieldSets$.next(updatedFieldSets);
    this.cdr.detectChanges();
    return;
  }

 // Get dependent fields data from api
  if (changedField?.reloadDataOnChange && event.value && event.value !== "-1") {

    const updatedFieldSetsImmediate = this.fieldSets.map(fieldSet => {
      fieldSet.fields = (fieldSet.fields ?? []).map(field => {
        if (field.dependsOn === changedField.name) {
          field.options = [{ id: -1, value: `Select ${field.label}` }];

          const depCtrl = this.reportsForm.get(field.name);
          if (depCtrl) depCtrl.setValue("");
        }
        return field;
      });
      return fieldSet;
    });

    this.fieldSets = [...updatedFieldSetsImmediate];
    this.reportService.fieldSets$.next(updatedFieldSetsImmediate);
    this.cdr.detectChanges();
  }

  if (changedField?.reloadDataOnChange && event.value && event.value !== '-1') {
    const params: Record<string, string> = {
      [changedField.name]: String(event.value)
    };

    const currentReportType = this.reportService.reportType;
    if (currentReportType) {
      this.apiService
        .getInputFieldOptionsForReport('report_type', currentReportType.type, params)
        .subscribe({
          next: (response) => {
            if (!response?.Data) return;
            const updatedResponse = response.Data;

            const updatedFieldSets = this.reportService.fieldSets$.value.map(fieldSet => {
              const fields = fieldSet.fields ?? [];

              fieldSet.fields = fields.map(field => {
                if (field.type === 'select' && field.name in updatedResponse) {
                  const apiFieldData = updatedResponse[field.name];
                  const baseOptions = apiFieldData.map((option: any) => {
                    if (Object.prototype.hasOwnProperty.call(option, 1)) {
                      return { id: option[1], value: `${option[0]} (${option[1]})` };
                    } else {
                      return { id: option[0], value: `${option[0]}` };
                    }
                  });

                  const noneSelected = { id: -1, value: `Select ${field.label}` };
                  field.options = !field.multiSelect ? [noneSelected, ...baseOptions] : baseOptions;
                }
                return field;
              });

              return fieldSet;
            });

            this.fieldSets = [...updatedFieldSets];
            this.reportService.fieldSets$.next(updatedFieldSets ?? []);
            this.cdr.detectChanges();
          },
          error: (err) => console.error(err)
        });
    }
  }
}

  public fieldSetsValidator(fieldSets: FieldSetValidators[]): ValidatorFn | null {

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return (control: AbstractControl): ValidationErrors | null => {

      const invalidFieldSets = fieldSets
      .filter(fieldSet => {
        const fieldValues = fieldSet.fieldNames.map(fieldName => this.reportsForm?.get(fieldName)?.value ?? '');
        const filledInFields = fieldValues.filter(fieldValue => fieldValue && (fieldValue.length || (!!fieldValue.start && !!fieldValue.end)));

        return filledInFields.length < fieldSet.minRequired;
      })

      const invalidFieldSetsNames = invalidFieldSets
      .map(fieldSet => fieldSet.fieldSetName)

      if (!invalidFieldSetsNames.length) {

        return null;
      }

      const required = {};
      invalidFieldSetsNames.forEach(fieldSet => required[fieldSet] = true);

      return { required }
    }
  }
}
